import { useCallback, useMemo, useRef, useState } from 'react';
import { getDashboardLoans } from '../../../js/dashboardService';
import {
  SkeletonLoansTable,
  SkeletonKPICard,
  SkeletonChart,
  SkeletonFilters,
  EmptyState,
  ErrorState,
} from '../../../components/ui/Admin/DashboardComponents/LoadingSkeletons/SkeletonKPICard';

import LoansFilters from '../../../components/ui/Admin/DashboardComponents/LoansFilters';
import KPICards from '../../../components/ui/Admin/DashboardComponents/KPICards';
import LoansTable from '../../../components/ui/Admin/DashboardComponents/LoansTable';
import ReportePrestamoDashboardExcel from '../../../components/ui/Admin/DashboardComponents/ReportePrestamoDashboardExcel';
import DailyBarChart from '../../../components/ui/Admin/DashboardComponents/FormComponents/DailyBarChart';
import AdvisorPieChart from '../../../components/ui/Admin/DashboardComponents/FormComponents/AdvisorPieChart';
import Paginacion from '../../../components/Reutilizables/Paginacion';
import MetricToggle from '../../../components/ui/Admin/DashboardComponents/MetricToggle';
import FetchWithGif from '../../../components/Reutilizables/FetchWithGif';

const validateFilters = (filters) => {
  const errors = {};
  const { date_start, date_end } = filters;
  const isISO = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value);

  if (date_start && !isISO(date_start)) {
    errors.date_start = 'Formato invalido. Use AAAA-MM-DD.';
  }

  if (date_end && !isISO(date_end)) {
    errors.date_end = 'Formato invalido. Use AAAA-MM-DD.';
  }

  if (date_start && date_end && isISO(date_start) && isISO(date_end)) {
    const start = new Date(date_start);
    const end = new Date(date_end);
    if (end < start) {
      errors.date_end = 'La fecha de fin debe ser posterior o igual a la fecha de inicio.';
    }
  }

  return errors;
};

const cleanParams = (filters) =>
  Object.fromEntries(
    Object.entries(filters).filter(([_, value]) => value !== '' && value != null)
  );

export default function DashboardLoansPage() {

  const [filters, setFilters] = useState({
    date_start: '',
    date_end: '',
    advisor: '',
    per_page: 50,
    modalidad: '',
    abonado_por: '',
    estado: '',
  });


  const [uiErrors, setUiErrors] = useState({});
  const [serverErrorMsg, setServerErrorMsg] = useState('');
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [showExcelGenerator, setShowExcelGenerator] = useState(false);

  const [chartMetric, setChartMetric] = useState('monto');

  const abortRef = useRef(null);
  const requestIdRef = useRef(0);
  const excelDataRef = useRef({ loans: [], meta: {} });

  const [state, setState] = useState({
    loading: false,
    error: '',
    rows: [],
    meta: {},
    charts: {},
    initialLoad: false,
  });

  const fetchData = useCallback(
    async (page = 1, isInitialLoad = false) => {
      const requestId = ++requestIdRef.current;
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      setState((s) => ({ ...s, loading: true, initialLoad: isInitialLoad }));
      setServerErrorMsg('');

      try {
        const data = await getDashboardLoans(cleanParams(filters), page, {
          signal: controller.signal,
        });
        if (requestId !== requestIdRef.current) return;
        setState({
          loading: false,
          error: '',
          rows: Array.isArray(data?.data) ? data.data : [],
          meta: data?.meta ?? {},
          charts: data?.charts ?? {},
          initialLoad: false,
        });
        setUiErrors({});
      } catch (e) {
        if (e?.name === 'AbortError') {
          if (requestId === requestIdRef.current) {
            setState((s) => ({ ...s, loading: false, initialLoad: false }));
          }
          return;
        }

        if (requestId !== requestIdRef.current) return;
        const status = e?.status || e?.response?.status;
        const payload = e?.response?.data || {};

        if (status === 422) {
          const mapped = {};
          const backendErrors = payload?.errors || {};
          if (backendErrors.date_end?.length) {
            mapped.date_end = backendErrors.date_end[0];
          }
          if (backendErrors.date_start?.length) {
            mapped.date_start = backendErrors.date_start[0];
          }
          setUiErrors(mapped);
          setServerErrorMsg(payload?.message || 'Revise los campos marcados.');
          setState((s) => ({ ...s, loading: false, initialLoad: false }));
          return;
        }

        setState((s) => ({
          ...s,
          loading: false,
          error: e?.message || 'No se pudo cargar el dashboard.',
          initialLoad: false,
        }));
      }
    },
    [filters]
  );

  const onChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: name === 'per_page' ? Number(value) : value,
    }));
    setUiErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const onApply = () => {
    const errors = validateFilters(filters);
    setUiErrors(errors);
    if (Object.keys(errors).length === 0) {
      fetchData(1, true);
    } else {
      setServerErrorMsg('Por favor, corrija los errores en las fechas.');
    }
  };

  const onResetToday = () => {
    setFilters({
      date_start: '',
      date_end: '',
      advisor: '',
      per_page: 50,
      modalidad: '',
      abonado_por: '',
      estado: '',
    });
    setUiErrors({});
    setServerErrorMsg('');
  };

  const handleExportExcel = useCallback(async () => {
    const errors = validateFilters(filters);
    setUiErrors(errors);

    if (Object.keys(errors).length > 0) {
      setServerErrorMsg('Por favor, corrija los errores en las fechas antes de exportar.');
      return;
    }

    setIsLoadingExport(true);
    setShowExcelGenerator(false);
    setServerErrorMsg('');

    try {
      const result = await getDashboardLoans(
        {
          ...cleanParams(filters),
          get_all: true,
        },
        1
      );

      const loans = Array.isArray(result?.data) ? result.data : [];
      if (loans.length === 0) {
        setServerErrorMsg('No hay préstamos para exportar con los filtros seleccionados.');
        return;
      }

      excelDataRef.current = {
        loans,
        meta: result?.meta ?? {},
      };
      setShowExcelGenerator(true);
    } catch (e) {
      const status = e?.status || e?.response?.status;
      const payload = e?.response?.data || {};

      if (status === 422) {
        const mapped = {};
        const backendErrors = payload?.errors || {};
        if (backendErrors.date_end?.length) {
          mapped.date_end = backendErrors.date_end[0];
        }
        if (backendErrors.date_start?.length) {
          mapped.date_start = backendErrors.date_start[0];
        }
        setUiErrors(mapped);
        setServerErrorMsg(payload?.message || 'Revise los campos marcados.');
        return;
      }

      setServerErrorMsg(e?.message || 'No se pudo generar el Excel.');
    } finally {
      setIsLoadingExport(false);
    }
  }, [filters]);

  const handleExcelComplete = useCallback(() => {
    excelDataRef.current = { loans: [], meta: {} };
    setShowExcelGenerator(false);
  }, []);

  const handleExcelGenerated = useCallback((url) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `Reporte_Prestamos_${filters.date_start || 'sin-inicio'}_${filters.date_end || 'sin-fin'}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [filters.date_end, filters.date_start]);

  const onGo = (page) => fetchData(page, false);
  const meta = useMemo(() => state?.meta ?? {}, [state?.meta]);
  const rows = useMemo(() => state?.rows ?? [], [state?.rows]);
  const charts = state?.charts ?? {};
  const chartsDaily = charts?.daily;
  const chartsByAdvisor = charts?.by_advisor;

  const totals = useMemo(
    () => ({
      capital: meta?.total_monto ?? 0,
      totalPagar: meta?.total_total ?? 0,
      totalPagarPendiente: meta?.total_saldo_pendiente ?? 0,
      capitalPendiente: meta?.total_capital_pendiente ?? 0,
      renta: meta?.total_intereses_globales ?? 0,
      rentaPendiente: meta?.total_renta_pendiente ?? 0,
      prestamos: meta?.total ?? 0,
      clientesRegistrados: meta?.clientes_registrados ?? 0,
      capitalPromedio: meta?.capital_promedio ?? 0,
      tasaPromedio: meta?.tasa_promedio ?? 0,
      rentaEjecutada: meta?.renta_ejecutada ?? 0,
      clientesNuevosConPrestamo: meta?.clientes_nuevos_con_prestamo ?? 0,
      prestamosInactivos: meta?.prestamos_inactivos ?? 0,
    }),
    [meta]
  );

  const dailyData = useMemo(() => {
    const source = Array.isArray(chartsDaily) ? chartsDaily : null;
    if (source && source.length > 0) {
      return source
        .map((row) => ({
          fecha: row?.fecha ? String(row.fecha) : 'Sin fecha',
          value: Math.round(Number(row?.[chartMetric] ?? 0) * 100) / 100,
        }))
        .sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)));
    }

    const key = chartMetric;
    const map = new Map();
    for (const r of rows) {
      const fecha = r?.fecha_generacion ?? 'Sin fecha';
      const value = Number(r?.[key] ?? 0);
      map.set(fecha, (map.get(fecha) || 0) + value);
    }
    return [...map.entries()]
      .map(([fecha, value]) => ({
        fecha: String(fecha),
        value: Math.round(value * 100) / 100,
      }))
      .sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)));
  }, [chartsDaily, rows, chartMetric]);

  const byAdvisorData = useMemo(() => {
    const source = Array.isArray(chartsByAdvisor) ? chartsByAdvisor : null;
    if (source && source.length > 0) {
      return source
        .map((row) => {
          const name =
            row?.name ||
            (row?.asesor_id != null ? `ID: ${row.asesor_id}` : 'Sin asesor');
          return {
            name: String(name),
            value: Math.round(Number(row?.[chartMetric] ?? 0) * 100) / 100,
          };
        })
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);
    }

    const key = chartMetric;
    const map = new Map();
    for (const r of rows) {
      const asesorName =
        r?.asesor?.toString()?.trim() ||
        (r?.asesor_id != null ? `ID: ${r.asesor_id}` : 'Sin asesor');
      const value = Number(r?.[key] ?? 0);
      map.set(asesorName, (map.get(asesorName) || 0) + value);
    }
    return [...map.entries()]
      .map(([name, value]) => ({ name: String(name), value: Math.round(value * 100) / 100 }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [chartsByAdvisor, rows, chartMetric]);

  const metricLabel = chartMetric === 'monto' ? 'Desembolso' : 'Total a pagar';

  const pagination = useMemo(
    () => ({
      pagina_actual: meta.current_page ?? 1,
      ultima_pagina: meta.last_page ?? 1,
      desde:
        meta.from ??
        (meta.current_page && meta.per_page
          ? (meta.current_page - 1) * meta.per_page + 1
          : 1),
      hasta:
        meta.to ??
        Math.min((meta.current_page ?? 1) * (meta.per_page ?? 50), meta.total ?? 0),
      total: meta.total ?? 0,
    }),
    [meta]
  );

  if (state.loading && state.initialLoad && rows.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">
          <header className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <div className="h-8 w-80 bg-gray-200 rounded mb-2 animate-pulse"></div>
              <div className="h-4 w-60 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="h-10 w-40 bg-gray-200 rounded-lg animate-pulse"></div>
          </header>

          <SkeletonFilters />

          <section className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <SkeletonKPICard key={i} />
            ))}
          </section>

          <section className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            <SkeletonChart title="Cargando gráfico diario..." />
            <SkeletonChart title="Cargando gráfico por asesor..." />
          </section>

          <SkeletonLoansTable />

          <div className="mt-6 flex flex-col md:flex-row items-center justify-between">
            <div className="h-4 w-60 bg-gray-200 rounded mb-2 md:mb-0 animate-pulse"></div>
            <div className="flex items-center space-x-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (state.error && rows.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <ErrorState
          title="Error al cargar el dashboard"
          message={state.error}
          onRetry={() => fetchData(1, true)}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {state.loading && !state.initialLoad && <FetchWithGif />}
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto space-y-6">
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="p-6 md:p-7">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-red-500">Panel de préstamos</p>
            <h1 className="mt-2 text-2xl font-bold text-slate-900">Visión consolidada de desembolsos</h1>
            <p className="mt-3 max-w-2xl text-sm text-slate-600">
              Monitorea capital colocado, nivel de avance y distribución operativa por asesor desde una sola vista.
            </p>
            <div className="mt-5">
              <MetricToggle value={chartMetric} onChange={setChartMetric} />
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Filtros de análisis</p>
              <h2 className="mt-1 text-lg font-semibold text-slate-900">Segmentación del periodo</h2>
            </div>
          </div>
          <LoansFilters
            filters={filters}
            onChange={onChange}
            onApply={onApply}
            onReset={onResetToday}
            loading={state.loading}
            errors={uiErrors}
            formError={serverErrorMsg}
          />
        </section>

        <KPICards totals={totals} />

        <section className="grid grid-cols-1 gap-6 lg:grid-cols-2 items-stretch">
          <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
            <DailyBarChart data={dailyData} title={`${metricLabel} por fecha`} loading={state.loading} />
          </div>
          <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
            <AdvisorPieChart data={byAdvisorData} title={`${metricLabel} por asesor`} loading={state.loading} />
          </div>
        </section>

        {rows.length === 0 && !state.loading ? (
          <div className="rounded-3xl border border-dashed border-gray-200 bg-white p-8 shadow-sm">
            <EmptyState
              title="No hay préstamos registrados"
              description="No se encontraron préstamos para los filtros seleccionados"
            />
          </div>
        ) : (
          <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
            <div className="mb-4 flex justify-end">
              <button
                onClick={handleExportExcel}
                disabled={isLoadingExport || state.loading || rows.length === 0}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 font-medium transition ${
                  isLoadingExport || state.loading || rows.length === 0
                    ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                    : 'bg-green-600 text-white shadow-md hover:bg-green-700 hover:shadow-lg'
                }`}
              >
                {isLoadingExport ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                    Generando Excel...
                  </>
                ) : (
                  <>
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Exportar a Excel
                  </>
                )}
              </button>
            </div>
            <LoansTable
              rows={rows}
              loading={state.loading}
              error={state.error}
              currentPage={meta.current_page ?? 1}
              itemsPerPage={meta.per_page ?? 50}
            />
          </div>
        )}

        {rows.length > 0 && (
          <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm">
            <Paginacion
              paginacion={pagination}
              alCambiarPagina={onGo}
              cargando={state.loading}
              nombreItem="préstamos"
              colorTema="red"
            />
          </div>
        )}

        {state.error && rows.length > 0 && (
          <div className="fixed bottom-4 right-4 z-50 max-w-sm">
            <div className="bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium">Error al actualizar</p>
                <p className="text-xs opacity-90">{state.error}</p>
              </div>
              <button
                onClick={() => setState((s) => ({ ...s, error: '' }))}
                className="text-white hover:bg-red-700 rounded p-1"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {showExcelGenerator && (
          <ReportePrestamoDashboardExcel
            loans={excelDataRef.current.loans}
            filters={filters}
            meta={excelDataRef.current.meta}
            onComplete={handleExcelComplete}
            onExcelGenerated={handleExcelGenerated}
          />
        )}
      </div>
    </div>
  );
}
